import type { JSX } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { VerifyGate } from "../VerifyReview";

// v3.2: the open document's own actions (Edit, Export, version history,
// Regenerate) sit in the visit's top bar ("…") and the note's meta line
// instead of a toolbar on the note. The document registers them here; those
// render them. Export goes through the document's "[verify]" review, so its
// gate travels with it.
export interface DocumentControls {
  canEdit: boolean;
  editing: boolean;
  startEditing: () => void;
  exportAction?: React.ReactNode;
  // Version history and Regenerate, as menu items.
  menuItems?: React.ReactNode;
  gate: VerifyGate | null;
  // Unresolved "[verify]" values, and opening their review.
  reviewCount: number;
  // Opens the first flagged value (undefined while it cannot be reviewed).
  openReview?: () => void;
  // Changes whenever the note text does, so the header picks up a new export.
  revision: string;
  // A card on the note (the review card, the sentence editor) has the
  // state's one primary action, so Copy to chart steps down meanwhile.
  overlay?: boolean;
}

interface Registry {
  current: React.MutableRefObject<DocumentControls | null>;
  bump: () => void;
  tick: number;
}

const RegistryContext = createContext<Registry | null>(null);

export const DocumentControlsProvider = ({
  children,
  controlsRef,
}: {
  children: React.ReactNode;
  // Lets the page read the open document's controls outside the provider
  // (Copy to chart's keyboard shortcut copies the document on screen).
  controlsRef?: React.MutableRefObject<DocumentControls | null>;
}): JSX.Element => {
  const own = useRef<DocumentControls | null>(null);
  const current = controlsRef ?? own;
  const [tick, setTick] = useState(0);
  const value = useMemo(
    () => ({ current, tick, bump: () => setTick((n) => n + 1) }),
    [tick],
  );
  return (
    <RegistryContext.Provider value={value}>
      {children}
    </RegistryContext.Provider>
  );
};

// The open document's controls (null while none is registered).
export const useDocumentControls = (): DocumentControls | null => {
  const registry = useContext(RegistryContext);
  return registry?.current.current ?? null;
};

// Registers this document's controls while it is mounted. Functions and
// nodes are read when used; the header re-renders when a shown state does.
export const useRegisterDocumentControls = (
  controls: DocumentControls | null,
): void => {
  const registry = useContext(RegistryContext);
  const bump = useRef(registry?.bump);
  bump.current = registry?.bump;
  const ref = registry?.current;
  useLayoutEffect(() => {
    if (ref && controls) ref.current = controls;
  });
  const active = !!controls;
  const key = controls
    ? [
        controls.canEdit,
        controls.editing,
        !!controls.exportAction,
        !!controls.menuItems,
        controls.reviewCount,
        controls.revision,
        !!controls.overlay,
      ].join("|")
    : "";
  useEffect(() => {
    if (!ref || !active) return;
    bump.current?.();
  }, [ref, active, key]);
  useEffect(
    () => () => {
      if (ref) ref.current = null;
      bump.current?.();
    },
    [ref],
  );
};
